import type { QueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/http.ts';
import {
  addItem,
  attachItemImage,
  checkItem,
  clearChecked,
  clearItemAisle,
  deleteItem,
  itemKeys,
  removeItemImage,
  setItemAisle,
  uncheckItem,
  updateItem,
} from '../features/items/itemsApi.ts';
import { pending, recordFailure, remove, type QueuedMutation } from './mutationQueue.ts';

/**
 * A queued mutation the server has refused this many times is not going to succeed.
 * Keeping it forever would block everything behind it in the queue, so it is dropped and
 * the subsequent reconcile puts the list back to whatever the server actually says.
 *
 * Counts only failures the server *answered*. A request that never arrived says nothing
 * about the mutation — and this is attempted on every reload, every resume and every
 * `online` event, so in a dead spot the count was really "how many times has this person
 * reopened the app". Five of those silently destroyed a change they had been shown as
 * saved, which is the one thing the queue exists to prevent.
 */
const MaxAttempts = 5;

export interface FlushResult {
  flushed: number;
  failed: number;
  /** Lists that had at least one mutation applied, and so need reconciling. */
  touchedLists: Set<string>;
}

/**
 * Sends everything queued, in order, then reconciles.
 *
 * <b>Order matters and is not negotiable.</b> "add milk" followed by "check milk" cannot
 * be sent concurrently — the check would arrive for an item that does not exist yet. So
 * this is a sequential loop, not Promise.all, and it stops at the first entry that
 * fails for a reason retrying could fix.
 */
export async function flushQueue(queryClient: QueryClient): Promise<FlushResult> {
  const entries = await pending();
  const touchedLists = new Set<string>();

  let flushed = 0;
  let failed = 0;

  for (const entry of entries) {
    if (entry.id === undefined) {
      continue;
    }

    try {
      await send(entry.mutation);
      await remove(entry.id);

      flushed += 1;
      touchedLists.add(entry.mutation.listId);
    } catch (error) {
      // Did the server answer at all? Everything below turns on it: an answer is
      // information about this mutation, and a TypeError from fetch is information about
      // the phone's radio.
      const answered = error instanceof ApiError;

      const permanent =
        answered &&
        error.isClientError &&
        // A 409 or 429 might succeed later; a 400 or 404 will not.
        error.status !== 409 &&
        error.status !== 429;

      if (permanent || (answered && entry.attempts + 1 >= MaxAttempts)) {
        // Give up on this one rather than blocking everything behind it. The reconcile
        // below restores whatever the server actually holds.
        await remove(entry.id);
        failed += 1;
        touchedLists.add(entry.mutation.listId);
        continue;
      }

      if (answered) {
        await recordFailure(entry);
      }

      // Almost certainly still offline. Stop here and keep the rest of the queue in
      // order for the next attempt.
      break;
    }
  }

  // Reconcile after the flush, not per mutation: a full fetch settles anything the
  // queue got wrong, including changes other people made while we were away.
  for (const listId of touchedLists) {
    await queryClient.invalidateQueries({ queryKey: itemKeys.list(listId) });
  }

  if (touchedLists.size > 0) {
    // Once, for every list: the chips come from the group's history, so a flush that
    // touched one list has moved them everywhere.
    await queryClient.invalidateQueries({ queryKey: itemKeys.allSuggestions });

    // And the aisle corrections, for the same reason and with the same scope. Cheap when
    // the flush held none — the query is a handful of short rows — and the alternative is
    // a correction made in a dead spot showing as the household's until the next reload.
    await queryClient.invalidateQueries({ queryKey: itemKeys.allAisles });
  }

  return { flushed, failed, touchedLists };
}

function send(mutation: QueuedMutation): Promise<unknown> {
  switch (mutation.kind) {
    case 'add':
      // Carries the id minted when the item was first added offline, so this is
      // idempotent even if an earlier attempt actually reached the server.
      return addItem(mutation.listId, {
        id: mutation.itemId,
        name: mutation.name,
        ...(mutation.note ? { note: mutation.note } : {}),
      });

    case 'check':
      return checkItem(mutation.itemId);

    case 'uncheck':
      return uncheckItem(mutation.itemId);

    case 'update':
      return updateItem(mutation.itemId, {
        name: mutation.name,
        note: mutation.note ?? null,
        // Entries queued before this field existed have it undefined, which lands as
        // "unassigned" — the same last-write-wins the name and note already get.
        assignedToUserId: mutation.assignedToUserId ?? null,
      });

    case 'delete':
      return deleteItem(mutation.itemId);

    case 'clearChecked':
      return clearChecked(mutation.listId);

    case 'attachImage':
      // Ordering is what makes this safe: the auto-increment key is the flush order, so
      // an attach queued behind the add of an item the server has never seen is sent
      // after it. Nothing here has to know that — it is a property of the queue.
      return attachItemImage(mutation.itemId, mutation.imageId, mutation.blob);

    case 'removeImage':
      return removeItemImage(mutation.itemId);

    case 'setAisle':
      // Idempotent at the server: one name has one aisle, so a replay is the same single
      // row rather than a second one. Ordering does the rest — a correction queued behind
      // the add of an item the server has never seen is sent after it.
      return setItemAisle(mutation.itemId, mutation.aisle);

    case 'clearAisle':
      return clearItemAisle(mutation.itemId);
  }
}
